
import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, ServiceItem, IntervalConfig, ServiceAssignment, ServiceTemplate, IntervalType, Customer, FlatRateCoverage, SifaBetreuungsgruppe, SifaBetriebsdaten, FrameworkContract, PositionsBestandteil } from '../types';
import { BETREUUNGSGRUPPEN, zahlAusEingabe } from './project-hub/sifaBetriebsdatenLogic';
import { Button } from './Button';
import { Building2, X, Hash, RefreshCw, Loader2, CheckCircle2, XCircle, MapPin, User, UserCheck, LayoutTemplate, Save, Sparkles, Database, Plus, Trash2, Search, ArrowRight, CheckSquare, AlertTriangle, ChevronDown, Link2, ListChecks } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { extractServicesFromDocument } from '../services/geminiService';
import { fetchCustomers } from '../services/crmService';
import {
    FRAMEWORK_VALIDITY_LABELS,
    FrameworkValidity,
    frameworkContractLabel,
    frameworkContractToServiceItems,
    frameworkContractValidity,
    frameworkContractsWithItems,
} from '../services/frameworkContractLogic';
import { localToday } from '../services/localToday';
import { CustomerPicker } from './crm/CustomerPicker';
import { resolveProjectCategories } from '../services/projectCategories';
import { NearbyProjectsPanel } from './NearbyProjectsPanel';
import { useNearbyProjects } from '../hooks/useNearbyProjects';
import { buildAddressQuery, coordinatorDistanceKm, formatDistanceKm } from '../services/nearbyProjects';
import { planungsPersonenAusAuswahl, profilName } from '../services/planungsZuweisung';
import { useRechte } from '../hooks/useRechte';
import { erlaubteAssistentArten, gueltigeAssistentArt } from '../services/projektAnlageRechte';
import { useGesperrteKonten } from '../hooks/useGesperrteKonten';
import { ohneGesperrte } from '../services/gesperrteKonten';
import { applyMinHoursInput, isHourlyUnit, katalogKontingentAusZeile, katalogKontingentFuerZeile } from '../services/projectLv';
import { isMonthlyUnit } from '../services/serviceUnits';
import { anker } from '../tutorial/anker';
import {
    CATALOG_FLAT_SCOPES,
    FLAT_SCOPE_LABELS,
    FlatScopeChoice,
    PROJECT_FLAT_SCOPES,
    applyFlatScopeChoice,
    flatPatchForUnitChange,
    flatRateSetupIssues,
    flatScopeChoiceOf,
    portableFlatRate,
    toggleExplicitServiceId,
} from '../services/flatRateSetup';
import { BestandteileEditor } from './bestandteile/BestandteileEditor';
import { StundenKontingentFeld } from './bestandteile/StundenKontingentFeld';
import {
    bestandteilePatch,
    definitionsFehlerImLv,
    mitVorlagenKopie,
    positionsSperreFuerBestandteile,
    pruefPosition,
    pruefeBestandteilListe,
} from './bestandteile/bestandteileEditorLogic';
import { hatBestandteile, kopiereVorlage, normalisiereBestandteile } from '../services/bestandteile/bestandteilLogic';
import { kontingentFuerKopie } from '../services/bestandteile/kontingentKopie';

interface Props {
  onComplete: (data: any) => void;
  onCancel: () => void;
  currentUser: UserProfile | null;
}

// Mindeststunden bleiben beim Anlegen BEWUSST leer: eine automatisch
// geschriebene 1 waere eine Vorgabe, die niemand vereinbart hat — der
// Buchungsdialog zeigte dann dauerhaft "Mindestens 1 Std. ansetzen." und
// warnte bei jeder halben Stunde. Das Feld traegt stattdessen einen
// Platzhalter und einen sichtbaren Hinweis, solange nichts eingetragen ist.

// Katalog-Quelle „Rahmenvertrag des Kunden": eigener Praefix im Quellen-Wert,
// damit eine Vertrags-id nie mit einer Vorlagen-id verwechselt wird.
const FC_SOURCE_PREFIX = 'fc:';
const isFrameworkSourceKey = (key: string) => key.startsWith(FC_SOURCE_PREFIX);

// Reihenfolge der Rahmenvertraege: gueltige zuerst, abgelaufene zuletzt.
const VALIDITY_RANK: Record<FrameworkValidity, number> = { active: 0, upcoming: 1, expired: 2 };

interface FrameworkSource {
    key: string;
    contract: FrameworkContract;
    validity: FrameworkValidity;
    /** Fertige LV-Positionen (vereinbarter Nettopreis), einmal je Vertrag erzeugt. */
    items: ServiceItem[];
}

export const ProjectSetupWizard: React.FC<Props> = ({ onComplete, onCancel, currentUser }) => {
    const [step, setStep] = useState(1);
    
    // Project Data
    const [projectName, setProjectName] = useState('');
    const [projectCategory, setProjectCategory] = useState('');
    const [projectSubcategory, setProjectSubcategory] = useState('');
    const [projectNumber, setProjectNumber] = useState('');
    const [isManualNumber, setIsManualNumber] = useState(false);
    const [numberStatus, setNumberStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
    const [isGeneratingNumber, setIsGeneratingNumber] = useState(false);
    const [selectedProjectType, setSelectedProjectType] = useState<string>('sigeko');
    /** SiFa = unbefristete Betreuung: Enddatum optional, kein Begehungsintervall (docs/sifa-unterweisung-plan.md §1). */
    const isSifa = selectedProjectType === 'sifa';
    // Betrieb (nur SiFa, docs/sifa-betreuung-plan.md): landet beim Anlegen in
    // metadata.sifa. Kompakt — alles Weitere pflegen die Stammdaten im Hub.
    const [sifaBeschaeftigte, setSifaBeschaeftigte] = useState('');
    const [sifaGruppe, setSifaGruppe] = useState<SifaBetreuungsgruppe | ''>('');
    const [sifaBg, setSifaBg] = useState('');

    // Address
    const [addrStreet, setAddrStreet] = useState('');
    const [addrZip, setAddrZip] = useState('');
    const [addrCity, setAddrCity] = useState('');
    // Geocoding + Umfeld-Check (Koordinatoren in der Naehe) — gemeinsamer Hook,
    // denselben nutzt die Auftragsannahme aus einem Angebot.
    const nearby = useNearbyProjects();
    const addrCoordinates = nearby.coordinates;
    const isGeocoding = nearby.geocoding;

    // Dates
    const [plannedStart, setPlannedStart] = useState(new Date().toISOString().split('T')[0]);
    const [plannedEnd, setPlannedEnd] = useState('');
    const [suggestedEndDate, setSuggestedEndDate] = useState<string | null>(null);
    const [, setIsManualEnd] = useState(false);
    const [intervalType, setIntervalType] = useState<IntervalType>('weekly');
    const [customDays, setCustomDays] = useState(14);
    
    // Clients
    const [clientName, setClientName] = useState('');
    const [clientAddress, setClientAddress] = useState('');
    const [clientContact, setClientContact] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    
    const [customerName, setCustomerName] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [customerContact, setCustomerContact] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');

    // CRM: Kundenauswahl (Phase 6). Auswahl befuellt die Auftraggeber-Felder vor und
    // haelt die Verknuepfung (customer_id). Nur-Admin-RLS: fetchCustomers kann fuer
    // Nicht-Admins fehlschlagen/leer sein -> nicht-fatal, Liste bleibt leer.
    const [crmCustomers, setCrmCustomers] = useState<Customer[]>([]);
    const [selectedCrmCustomerId, setSelectedCrmCustomerId] = useState<string>('');

    // Bewusst LEER: keine Vorauswahl. Sonst wurde still der angemeldete Nutzer
    // als Koordinator uebernommen, obwohl jemand anderes betreuen sollte.
    const [assignedUserId, setAssignedUserId] = useState<string>('');
    // Verantwortliche Planungsphase (Projekt-Kickoff, docs/projekt-kickoff-plan.md
    // §2/§5, Entscheid 12.10.): mehrere Personen möglich, leer = der
    // Hauptverantwortliche plant selbst (Normalfall). Die Gewählten landen mit
    // Bereich 'planung' in der Vertreterliste, nicht als Besitzer — Besitzer
    // bleibt der Hauptverantwortliche (Ausführung).
    const [planningUserIds, setPlanningUserIds] = useState<string[]>([]);
    const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
    // Gesperrte Mitglieder (Nr. 148) stehen in keiner Auswahl mehr; eine
    // schon gewählte Person bleibt sichtbar, damit das Feld nicht still springt.
    const gesperrteKonten = useGesperrteKonten();
    
    // Services
    const [selectedServices, setSelectedServices] = useState<ServiceItem[]>([]);
    // Rohtext der Mindeststunden-Felder je Position. Getrennt vom Modellwert,
    // damit Zwischenstufen wie "0" oder "0." beim Tippen von "0.25" stehen
    // bleiben — aus dem Modell zurueckgespeist verschwaenden sie sofort und die
    // naechsten Ziffern ergaeben 25.
    const [minHoursText, setMinHoursText] = useState<Record<string, string>>({});
    const [catalogItems, setCatalogItems] = useState<ServiceItem[]>([]);
    const [templates, setTemplates] = useState<ServiceTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [catalogSearch, setCatalogSearch] = useState('');
    
    const [isCatalogOpen, setIsCatalogOpen] = useState(false);
    const [isCatalogLoading, setIsCatalogLoading] = useState(false);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [selectionBuffer, setSelectionBuffer] = useState<Map<string, number>>(new Map());

    const [newItem, setNewItem] = useState<{
        lvPosition: string;
        title: string;
        unit: string;
        unitPrice: number;
        category: string;
        isDefault: boolean;
        assignment: ServiceAssignment;
        flatRate?: FlatRateCoverage;
        /** Bestandteil-Vorlage der neuen Katalogposition (nur Admin, E6). */
        bestandteile?: PositionsBestandteil[];
        /** Vorgabe des Stundenkontingents — nur mit Bestandteil-Vorlage (S1, Nr. 171). */
        stundenKontingent?: number;
    }>({
        lvPosition: '', title: '', unit: 'Stück', unitPrice: 0, category: 'Ausführung', isDefault: false, assignment: 'none'
    });

    const lvFileInputRef = useRef<HTMLInputElement>(null);
    // PAKETPOSITION: Zeilen der LV-Tabelle mit aufgeklappten Bestandteilen,
    // und ob der Editor bei „Neue Position erstellen" offen ist.
    const [offeneBestandteile, setOffeneBestandteile] = useState<Set<string>>(new Set());
    const [neuBestandteileAuf, setNeuBestandteileAuf] = useState(false);
    // Ungueltige Vorlage beim Erstellen: nach dem ersten Klick steht die
    // Meldung unter „Erstellen & Speichern" — live aus newItem, sie
    // verschwindet also, sobald die Liste passt.
    const [neuVorlageGeprueft, setNeuVorlageGeprueft] = useState(false);
    // Ungueltige Eingabe im Stundenkontingent der neuen Katalogposition —
    // sperrt „Erstellen & Speichern" wie eine ungueltige Vorlage (S1/S9).
    const [neuKontingentFehler, setNeuKontingentFehler] = useState<string | null>(null);

    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
    // Rollensystem (docs/rollensystem-plan.md §4). admin und super_admin haben
    // jedes Recht; ohne Rechte aus der Datenbank gilt das bisherige Verhalten.
    const rechte = useRechte(currentUser?.role);
    // Hauptverantwortlichen und Planung wählen: Admin oder Recht
    // `projekte.zuweisen`. Alle anderen legen das Projekt für sich selbst an.
    const darfZuweisen = isAdmin || rechte.hat('projekte.zuweisen');
    // Angeboten werden nur die Typen, die man anlegen darf
    // (`projekte.<typ>_anlegen`, services/projektAnlageRechte.ts). Ist die
    // Wahl nicht (mehr) erlaubt — anfangs SiGeKo, oder die Rechte kommen erst
    // nach dem Öffnen —, springt sie auf den ersten erlaubten Typ; wer nur
    // einen darf, hat ihn damit vorgewählt.
    const erlaubteArten = React.useMemo(() => erlaubteAssistentArten(rechte.hat), [rechte]);
    const gueltigeArt = gueltigeAssistentArt(selectedProjectType, erlaubteArten);
    useEffect(() => {
        if (gueltigeArt && gueltigeArt !== selectedProjectType) setSelectedProjectType(gueltigeArt);
    }, [gueltigeArt, selectedProjectType]);
    // Kategorienkatalog aus dem Profil, sonst Standard (services/projectCategories.ts).
    const projectCategories = resolveProjectCategories(currentUser?.settings);

    const assignmentOptions: {id: ServiceAssignment, label: string}[] = [
        { id: 'none', label: 'Keine / Sonstige' },
        { id: 'inspection', label: 'Begehung' },
        { id: 'vorank', label: 'Vorankündigung' },
        { id: 'unterlage', label: 'Unterlage (RAB)' },
        { id: 'sigeplan', label: 'SiGe-Plan' },
        { id: 'asplan', label: 'A+S-Plan' },
        { id: 'einweisung', label: 'Einweisung SiGe-Plan' },
        { id: 'site_rules', label: 'Baustellenordnung' },
        { id: 'planning_general', label: 'Planung Allg.' },
        { id: 'additional', label: 'Zusatz' },
        // SiFa: Stundenposition fuer „Zeit buchen" in der Gefaehrdungsbeurteilung
        // (docs/sifa-gbu-plan.md §10; optional, Entscheid O3).
        { id: 'gbu', label: 'Gefährdungsbeurteilung (SiFa)' },
        // Nur Anzeige: 'flatrate' entsteht ausschliesslich ueber das
        // Deckungs-Select bei Einheit 'Monat' (flatRateSetup) — direkt
        // waehlbar entstuende die halb angelegte Pauschale ohne Deckung.
        { id: 'flatrate', label: 'Monatspauschale' },
    ];

    // --- Init ---
    useEffect(() => {
        const fetchUsers = async () => {
             const { data } = await supabase.from('profiles').select('*');
             if(data) setAvailableUsers(data);
        };
        fetchUsers();
    }, []);

    // CRM-Kunden einmalig laden. Nicht-fatal: bei Fehler (z.B. Nicht-Admin ohne
    // RLS-Zugriff) bleibt die Liste leer und der Picker wird nicht gerendert.
    useEffect(() => {
        let cancelled = false;
        fetchCustomers()
            .then((list) => { if (!cancelled) setCrmCustomers(list); })
            .catch((err) => { console.warn('CRM-Kunden konnten nicht geladen werden:', err); });
        return () => { cancelled = true; };
    }, []);

    // Ein gewaehlter Rahmenvertrag gehoert zum bisherigen Kunden — beim
    // Wechsel/Loesen zurueck auf den Gesamt-Katalog. Vorlagen bleiben stehen.
    const resetFrameworkSource = () => {
        setSelectedTemplateId(prev => (isFrameworkSourceKey(prev) ? '' : prev));
    };

    // Auswahl eines CRM-Kunden: Auftraggeber-Felder vorbefuellen + Verknuepfung setzen.
    // Manuelles Editieren der Felder bleibt moeglich und loest die Verknuepfung nicht.
    const handleSelectCrmCustomer = (c: Customer) => {
        const primaryContact = c.contacts?.[0];
        if (c.id !== selectedCrmCustomerId) resetFrameworkSource();
        setSelectedCrmCustomerId(c.id);
        setCustomerName(c.name || '');
        setCustomerAddress(c.address || '');
        setCustomerContact(primaryContact?.name || '');
        // Kontaktdaten des ersten Ansprechpartners bevorzugen, Kunde als Fallback.
        setCustomerPhone(primaryContact?.phone || c.phone || '');
        setCustomerEmail(primaryContact?.email || c.email || '');
    };

    // Nur die Verknuepfung loesen; die vorbefuellten Textfelder bleiben erhalten.
    const handleUnlinkCrmCustomer = () => {
        resetFrameworkSource();
        setSelectedCrmCustomerId('');
    };

    // Catalog & Templates
    const refreshCatalog = async () => {
        setIsCatalogLoading(true);
        const { data } = await supabase.from('service_catalog').select('*').order('lv_position');
        if (data) {
             const items = data.map((d: any) => {
                 // Bestandteil-Vorlage einer Paketposition (Spalte
                 // service_catalog.bestandteile, SQL Nr. 165) — als Kopie,
                 // normalisiert. Fehlt die Spalte, bleibt das Feld weg und der
                 // Wizard arbeitet wie bisher (Muster flat_rate).
                 const vorlage = kopiereVorlage(normalisiereBestandteile(d.bestandteile));
                 // Stundenkontingent der Vorlage (service_catalog.stunden_kontingent,
                 // SQL Nr. 171) — reist NUR zusammen mit den Bestandteilen (S1).
                 const kontingent = vorlage ? katalogKontingentAusZeile(d) : undefined;
                 return {
                     id: d.id,
                     lvPosition: d.lv_position,
                     title: d.title,
                     unit: d.unit,
                     unitPrice: d.unit_price,
                     category: d.category,
                     isDefault: d.is_default,
                     assignment: d.assignment || 'none',
                     // Deckungsumfang einer Katalog-Pauschale reist mit ins Projekt
                     // (Spalte service_catalog.flat_rate, SQL Nr. 54).
                     flatRate: d.flat_rate ?? undefined,
                     ...(vorlage ? { bestandteile: vorlage } : {}),
                     ...(kontingent !== undefined ? { stundenKontingent: kontingent } : {}),
                     quantityContracted: 1,
                     quantityUsed: 0,
                     status: 'open'
                 };
             });
             setCatalogItems(items);
        }
        setIsCatalogLoading(false);
    };

    const refreshTemplates = async () => {
        const { data } = await supabase.from('service_templates').select('*');
        if (data) setTemplates(data);
    };

    useEffect(() => {
        refreshCatalog();
        refreshTemplates();
    }, []);

    // Project Number Generation
    const generateProjectNumber = async () => {
        setIsGeneratingNumber(true);
        
        // 1. Prefix ermitteln (JJMM + Typ)
        const datePart = new Date().toISOString().slice(2, 4) + (new Date().getMonth() + 1).toString().padStart(2, '0');
        const prefix = selectedProjectType === 'sigeko' ? '1' : (selectedProjectType === 'sifa' ? '2' : '3');
        const baseStr = `${datePart}${prefix}`; // z.B. "26021"

        // 2. Höchste existierende Nummer mit diesem Prefix suchen
        const { data } = await supabase
            .from('projects')
            .select('project_number')
            .ilike('project_number', `${baseStr}%`) // Filter: Beginnt mit Prefix
            .order('project_number', { ascending: false }) // Sortieren: Höchste zuerst
            .limit(1);

        let nextSeq = 1;

        if (data && data.length > 0 && data[0].project_number) {
            // Höchste Nummer gefunden (z.B. "26021023")
            const lastNumber = data[0].project_number;
            // Suffix extrahieren (alles nach dem Prefix) -> "023"
            const sequencePart = lastNumber.replace(baseStr, '');
            const lastSeq = parseInt(sequencePart, 10);
            
            if (!isNaN(lastSeq)) {
                // Nächste Nummer = Letzte + 1
                nextSeq = lastSeq + 1;
            }
        }
        
        // 3. Formatieren (Padding auf mind. 3 Stellen, z.B. 024)
        const seq = String(nextSeq).padStart(3, '0');
        
        setProjectNumber(`${baseStr}${seq}`);
        setNumberStatus('available');
        setIsGeneratingNumber(false);
    };
    
    useEffect(() => {
        if (!isManualNumber) generateProjectNumber();
    }, [selectedProjectType, isManualNumber]);

    const checkNumberAvailability = async (num: string) => {
        setNumberStatus('checking');
        const { data } = await supabase.from('projects').select('id').eq('project_number', num).maybeSingle();
        if (data) setNumberStatus('taken');
        else setNumberStatus('available');
    };

    // Adresse verorten und Umfeld pruefen (Nominatim + Projekte im Umkreis).
    // Nicht-fatal: ohne Treffer bleibt die Anlage moeglich, nur ohne Entfernungen.
    const handleGeocode = () => {
        const query = buildAddressQuery({ street: addrStreet, zip: addrZip, city: addrCity });
        if (!query) return;
        void nearby.lookup(query);
    };

    const addServiceFromCatalog = (item: ServiceItem) => {
        if (selectedServices.some(s => s.lvPosition === item.lvPosition)) return;
        // Bestandteile TIEF kopieren (Kernregel 8): die Projektposition darf
        // sich keine Liste mit Katalog oder Vorlage teilen.
        setSelectedServices([...selectedServices, mitVorlagenKopie({ ...item, id: crypto.randomUUID() })]);
    };

    const toggleBestandteile = (id: string) => {
        setOffeneBestandteile(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // --- LOAD TEMPLATE LOGIC ---
    const handleLoadTemplate = (templateId: string) => {
        setSelectedTemplateId(templateId);
        setCatalogSearch(''); 
    };

    // --- SAVE AS TEMPLATE LOGIC ---
    const handleSaveAsTemplate = async () => {
        if (selectedServices.length === 0) return alert("Die Liste ist leer.");
        
        const name = prompt("Bitte geben Sie einen Namen für die neue Vorlage ein:");
        if (!name) return;

        setIsSavingTemplate(true);
        try {
            const templateItems = selectedServices.map(s => {
                // Bestandteile reisen als Kopie in die Vorlage — sie sind
                // Teil dessen, was die Position ist, nicht ihr Projektstand.
                // Das Stundenkontingent NUR mit ihnen (S1).
                const bestandteile = kopiereVorlage(s.bestandteile);
                return {
                    lvPosition: s.lvPosition,
                    title: s.title,
                    unit: s.unit,
                    // Fix: s is a ServiceItem, which uses camelCase unitPrice property correctly here.
                    unitPrice: s.unitPrice,
                    category: s.category,
                    quantityContracted: s.quantityContracted,
                    quantityUsed: 0,
                    status: 'open',
                    assignment: s.assignment,
                    // 'explicit'-Deckungen bleiben im Projekt (ihre Ids waeren in
                    // der Vorlage Phantome); regelbasierte Umfaenge reisen mit.
                    flatRate: portableFlatRate(s.flatRate),
                    ...(bestandteile ? { bestandteile } : {}),
                    ...kontingentFuerKopie({ bestandteile }, s.stundenKontingent),
                };
            });

            const payload = {
                name: name,
                description: `Erstellt aus Projekt-Wizard am ${new Date().toLocaleDateString()}`,
                items: templateItems,
                updated_at: new Date().toISOString()
            };

            const { error } = await supabase.from('service_templates').insert([payload]);
            if (error) throw error;
            
            await refreshTemplates();
            alert("Vorlage erfolgreich gespeichert!");
        } catch (e: any) {
            alert("Fehler beim Speichern der Vorlage: " + e.message);
        } finally {
            setIsSavingTemplate(false);
        }
    };

    // Bestandteil-Vorlage (nur Admin): eine ungueltige Liste (leeres
    // Label, auf einer Pauschale) gar nicht erst in den Katalog schreiben.
    const neuVorlagenFehler = isAdmin && hatBestandteile(newItem)
        ? pruefeBestandteilListe(newItem.bestandteile || [], pruefPosition({ unit: newItem.unit, assignment: newItem.assignment, flatRate: newItem.flatRate }))
        : null;

    const handleCreateGlobalService = async () => {
        if (!newItem.title) return alert("Bitte Titel eingeben");

        if (neuVorlagenFehler || (isAdmin && hatBestandteile(newItem) && neuKontingentFehler)) {
            // Meldung inline unter dem Knopf, der Editor geht dazu auf.
            setNeuVorlageGeprueft(true);
            setNeuBestandteileAuf(true);
            return;
        }

        setIsCatalogLoading(true);
        try {
            if (isAdmin) {
                const dbPayload = {
                    lv_position: newItem.lvPosition,
                    title: newItem.title,
                    unit: newItem.unit,
                    unit_price: newItem.unitPrice,
                    category: newItem.category,
                    is_default: newItem.isDefault,
                    assignment: newItem.assignment,
                    // Nur mitschicken, wenn gesetzt: Positionen ohne Pauschale
                    // bleiben auch ohne die Spalte flat_rate (SQL Nr. 54) anlegbar.
                    ...(newItem.flatRate ? { flat_rate: newItem.flatRate } : {}),
                    // Dasselbe fuer die Bestandteil-Vorlage (Spalte
                    // service_catalog.bestandteile, SQL Nr. 165).
                    ...(hatBestandteile(newItem) ? { bestandteile: newItem.bestandteile } : {}),
                    // Und das Stundenkontingent (service_catalog.stunden_kontingent,
                    // SQL Nr. 171) — nur mit Bestandteil-Vorlage, nur gueltig.
                    ...katalogKontingentFuerZeile(newItem)
                };
                const { error } = await supabase.from('service_catalog').insert([dbPayload]);
                if (error) throw error;
                await refreshCatalog(); 
                alert("Position im globalen Katalog gespeichert.");
            } else {
                if (selectedServices.some(s => s.lvPosition === newItem.lvPosition)) {
                    alert(`Position ${newItem.lvPosition} ist bereits im Projekt vorhanden.`);
                    return;
                }

                const localItem = {
                    ...newItem,
                    id: crypto.randomUUID(),
                    quantityContracted: 1,
                    quantityUsed: 0,
                    status: 'open' as const
                };
                setSelectedServices([...selectedServices, localItem]);
                alert("Erfolgreich hinzugefügt");
            }
            
            setNewItem({
                lvPosition: '',
                title: '',
                unit: 'Stück',
                unitPrice: 0,
                category: 'Ausführung',
                isDefault: false,
                assignment: 'none'
            });
            setNeuBestandteileAuf(false);
            setNeuVorlageGeprueft(false);
            setNeuKontingentFehler(null);

        } catch (e: any) {
            alert("Fehler beim Speichern: " + e.message);
        } finally {
            setIsCatalogLoading(false);
        }
    };

    const updateService = (id: string, field: keyof ServiceItem, value: any) => {
        setSelectedServices(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    // Mehrere Felder auf einmal: die Monatspauschale aendert assignment und
    // flatRate immer als Paar (flatRateSetup.FlatSetupPatch).
    const patchService = (id: string, patch: Partial<ServiceItem>) => {
        setSelectedServices(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
    };

    // Pruefung der Pauschal-Anlage (flatRateSetup): errors = unvollstaendig
    // (halbe Pauschale / 'explicit' ohne Auswahl), warnings = auffaellig, aber
    // erlaubt. Die Regeln liegen als pure Funktionen im Service-Modul, der
    // Wizard zeigt sie nur an.
    const flatIssues = React.useMemo(() => flatRateSetupIssues(selectedServices), [selectedServices]);
    const flatErrors = flatIssues.filter(i => i.level === 'error');
    // PAKETPOSITION: Bestandteile, die nicht zur Position passen (E5: nur
    // Menge 1, nicht auf einer Pauschale; leeres Label) sperren die Anlage —
    // gespeichert stuende sonst eine Paketposition, deren Gate niemand deuten
    // kann. Die Regel selbst ist pruefeDefinition (bestandteilLogic).
    const bestandteilFehler = React.useMemo(() => definitionsFehlerImLv(selectedServices), [selectedServices]);

    const removeService = (id: string) => {
        setSelectedServices(prev => prev.filter(item => item.id !== id));
    };

    const handleLvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsAiLoading(true);
        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const base64 = event.target?.result as string;
                try {
                    const extractedItems = await extractServicesFromDocument(base64, file.type);
                    
                    if (extractedItems && extractedItems.length > 0) {
                        const existingPositions = new Set(selectedServices.map(s => s.lvPosition));
                        
                        const newServices = extractedItems
                            .filter((item: any) => !existingPositions.has(item.lvPosition))
                            .map((item: any) => ({
                                id: crypto.randomUUID(),
                                lvPosition: item.lvPosition || '',
                                title: item.title,
                                unit: item.unit,
                                quantityContracted: item.quantityContracted,
                                quantityUsed: 0,
                                unitPrice: item.unitPrice,
                                status: 'open' as const,
                                category: item.category,
                                dueDate: plannedStart || undefined,
                                assignment: item.assignment || 'none'
                            }));
                        
                        setSelectedServices(prev => [...prev, ...newServices]);
                        
                        if (newServices.length > 0) {
                            alert(`${newServices.length} Positionen erfolgreich importiert.`);
                        } else {
                            alert("Keine neuen Positionen importiert (Duplikate übersprungen).");
                        }
                    } else {
                        alert("Keine Positionen erkannt.");
                    }
                } catch (err) {
                    alert("Fehler bei der KI-Analyse des Dokuments.");
                    console.error(err);
                } finally {
                    setIsAiLoading(false);
                }
            };
            reader.readAsDataURL(file);
        } catch {
            setIsAiLoading(false);
        }
        e.target.value = '';
    };

    const handleNext = () => {
        if (!projectName.trim()) return alert("Bitte Projektnamen eingeben.");
        if (isManualNumber && numberStatus === 'taken') return alert("Diese Projektnummer ist bereits vergeben.");
        if (!projectNumber.trim()) return alert("Bitte Projektnummer vergeben.");
        // Pflichtfeld: ohne aktive Auswahl wurde frueher still der angemeldete
        // Nutzer zugewiesen.
        if (darfZuweisen && !assignedUserId) return alert("Bitte einen Hauptverantwortlichen (Ausführung, Projektbesitzer) auswählen.");

        // SiFa: der Betreuungsbeginn liegt regelmaessig in der Vergangenheit
        // (laufende Betreuung wird nacherfasst) — keine Rueckfrage.
        const today = new Date();
        today.setHours(0,0,0,0);
        const start = new Date(plannedStart);
        if (!isSifa && start < today) {
            if (!confirm("Das Startdatum liegt in der Vergangenheit. Möchten Sie trotzdem fortfahren?")) return;
        }

        // Trigger Geocoding if not done yet
        if (!addrCoordinates && addrCity && addrStreet) {
            handleGeocode();
        }

        setStep(2);
    };

    const handleFinish = () => {
        // Unvollstaendige Pauschale blockt die Anlage: eine als Pauschale
        // markierte Position ohne Deckungsumfang waere genau die wirkungslose
        // Monatspauschale, gegen die dieser Schritt gebaut ist. Warnungen
        // blocken bewusst NICHT — sie sind eine Entscheidung des Bearbeiters.
        if (flatErrors.length > 0) {
            alert('Monatspauschale unvollständig:\n\n' + flatErrors.map(i => '• ' + i.message).join('\n'));
            setStep(2);
            return;
        }
        if (bestandteilFehler.length > 0) {
            // Die Meldung steht als roter Kasten ueber der LV-Tabelle
            // (Schritt 2); der Knopf ist dort ohnehin gesperrt.
            setStep(2);
            return;
        }

        // Ohne Recht auf den gewählten Typ keine Anlage — die Typwahl bietet
        // ihn gar nicht erst an, das hier ist nur die zweite Reihe.
        if (!gueltigeArt || gueltigeArt !== selectedProjectType) {
            setStep(1);
            return;
        }

        // Wer zuweisen darf, muss aktiv waehlen; fuer alle anderen ist die
        // Auswahl nicht sichtbar und es bleibt beim angemeldeten Nutzer.
        if (darfZuweisen && !assignedUserId) {
            alert("Bitte einen Hauptverantwortlichen (Ausführung, Projektbesitzer) auswählen (Schritt 1).");
            setStep(1);
            return;
        }

        let finalAssignedUser: UserProfile | undefined;

        if (darfZuweisen && assignedUserId) {
            finalAssignedUser = availableUsers.find(u => u.id === assignedUserId);
        }

        if (!finalAssignedUser && currentUser) {
            finalAssignedUser = currentUser;
        }

        // Verantwortliche Planungsphase nur, wenn jemand mit Zuweisungsrecht
        // aktiv ANDERE Personen gewählt hat: ohne den Hauptverantwortlichen
        // (sonst eine doppelte Kickoff-Quittung für ihn), ohne Dubletten, bei
        // SiFa nie (keine Planungsphase — auch wenn vor dem Typwechsel jemand
        // gewählt war). Regeln in services/planungsZuweisung.ts.
        const planningUsers = planungsPersonenAusAuswahl(
            planningUserIds, availableUsers, finalAssignedUser?.id, { darfZuweisen, isSifa });

        // SiFa-Dauerprojekt: keine Begehungsintervalle vorbelegen — die
        // Betreuung ist unbefristet und hat keinen Begehungsrhythmus aus dem
        // Anlagedialog. Der Hub faellt auf seine Vorgabe zurueck (ProjectHub:
        // `meta.intervalConfig || { type: 'weekly' }`), der Ladepfad prueft das
        // Feld vor dem Zugriff (cloudService).
        const intervalConfig: IntervalConfig | undefined = isSifa ? undefined : {
            type: intervalType,
            customDays: intervalType === 'custom' ? customDays : undefined
        };
        
        const fullAddress = `${addrStreet}, ${addrZip} ${addrCity}`.trim().replace(/^, /, '').replace(/, $/, '');

        // Betriebsdaten nur bei SiFa und nur mit tatsaechlich gefuellten
        // Feldern — ein leeres `sifa: {}` haette im JSON nichts zu sagen.
        const sifaDaten: SifaBetriebsdaten = {};
        if (isSifa) {
            const beschaeftigte = zahlAusEingabe(sifaBeschaeftigte);
            if (beschaeftigte !== undefined) sifaDaten.beschaeftigte = beschaeftigte;
            if (sifaGruppe) sifaDaten.betreuungsgruppe = sifaGruppe;
            if (sifaBg.trim()) sifaDaten.berufsgenossenschaft = sifaBg.trim();
        }

        onComplete({
            projectName,
            // SiFa: keine Bau-Kategorie — die Auswahl ist ausgeblendet und
            // bleibt leer, auch wenn vor dem Typwechsel etwas gewaehlt war.
            projectCategory: isSifa ? '' : projectCategory,
            projectSubcategory: isSifa ? '' : projectSubcategory,
            // Eine Betreuung startet laufend, nicht in Planung (Plan §1);
            // App.handleSetupComplete setzt den Status und metadata.sifa.
            initialStatus: isSifa ? 'active' : 'planning',
            sifa: isSifa && Object.keys(sifaDaten).length > 0 ? sifaDaten : undefined,
            projectNumber, 
            address: fullAddress, 
            services: selectedServices, 
            startDate: plannedStart,
            // Bei SiFa darf das Ende leer bleiben (unbefristet); ein leerer
            // String stuende sonst als „Ende" im Projekt.
            endDate: plannedEnd || undefined,
            intervalConfig,
            // Nur die Typen, die metadata.projectType kennt ('digital'/'other'
            // steuern bisher allein die Nummernvergabe und bleiben SiGeKo).
            projectType: isSifa ? 'sifa' : selectedProjectType === 'hse' ? 'hse' : 'sigeko',
            assignedUser: finalAssignedUser,
            // App.handleSetupComplete hängt die Personen mit bereich 'planung'
            // an die Vertreterliste (Kickoff-Plan §2/§5). Fehlt = der
            // Hauptverantwortliche plant selbst.
            planningUsers: planningUsers.length > 0 ? planningUsers : undefined,
            clientName,
            customerName,
            clientDetails: { address: clientAddress, contact: clientContact, phone: clientPhone, email: clientEmail },
            customerDetails: { address: customerAddress, contact: customerContact, phone: customerPhone, email: customerEmail },
            crmCustomerId: selectedCrmCustomerId || undefined,
            coordinates: addrCoordinates || undefined
        });
    };

    // Rahmenvertraege des gewaehlten CRM-Kunden mit vereinbarten Preisen.
    // Kunden laedt nur der Innendienst (RLS) — ohne geladene Kunden gibt es
    // keinen gewaehlten Kunden und damit auch keine Vertragsquelle.
    // Die Positionen entstehen hier EINMAL je Vertrag: frische ids bei jedem
    // Render liessen Auswahl und „Im LV"-Abgleich ins Leere laufen.
    const selectedCrmCustomer = React.useMemo(
        () => crmCustomers.find(c => c.id === selectedCrmCustomerId) || null,
        [crmCustomers, selectedCrmCustomerId],
    );
    const frameworkSources = React.useMemo<FrameworkSource[]>(() => {
        if (!selectedCrmCustomer) return [];
        const today = localToday();
        return frameworkContractsWithItems(selectedCrmCustomer)
            .map(contract => ({
                key: FC_SOURCE_PREFIX + contract.id,
                contract,
                validity: frameworkContractValidity(contract, today),
                items: frameworkContractToServiceItems(contract),
            }))
            // Gueltige zuerst, darin der Standard-Rahmenvertrag vorn.
            .sort((a, b) =>
                VALIDITY_RANK[a.validity] - VALIDITY_RANK[b.validity]
                || Number(!!b.contract.isDefault) - Number(!!a.contract.isDefault));
    }, [selectedCrmCustomer]);
    const activeFrameworkSource = isFrameworkSourceKey(selectedTemplateId)
        ? frameworkSources.find(s => s.key === selectedTemplateId) ?? null
        : null;

    // EINE Aufloesung der Katalog-Quelle fuer Liste und „Auswahl uebernehmen":
    // '' = Gesamt-Katalog, 'fc:<id>' = Rahmenvertrag des Kunden, sonst Vorlage.
    const activeSourceItems = React.useMemo<ServiceItem[]>(() => {
        if (isFrameworkSourceKey(selectedTemplateId)) {
            return frameworkSources.find(s => s.key === selectedTemplateId)?.items ?? [];
        }
        if (selectedTemplateId) {
            const tmpl = templates.find(t => t.id === selectedTemplateId);
            return tmpl ? tmpl.items : [];
        }
        return catalogItems;
    }, [selectedTemplateId, templates, catalogItems, frameworkSources]);

    const filteredSourceItems = activeSourceItems.filter(item =>
        (item.title || '').toLowerCase().includes(catalogSearch.toLowerCase()) ||
        (item.lvPosition || '').toLowerCase().includes(catalogSearch.toLowerCase())
    );

    // Katalog direkt mit einem Rahmenvertrag als Quelle oeffnen (Hinweis im LV-Schritt).
    const openCatalogWithSource = (key: string) => {
        handleLoadTemplate(key);
        setIsCatalogOpen(true);
    };

    const calculateTotal = () => {
        return selectedServices.reduce((sum, item) => sum + (item.quantityContracted * item.unitPrice), 0);
    };

    // Kein Typ erlaubt: Hinweis statt Formular. Der Einstieg („Neues
    // Projekt") ist dann eigentlich schon ausgeblendet — das hier fängt ab,
    // was trotzdem durchkommt (Rechte erst nach dem Öffnen geladen).
    if (erlaubteArten.length === 0) {
        return (
            <div data-glass-view="projekt-dialog" className="fixed inset-0 z-[150] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                <div role="dialog" aria-modal="true" aria-labelledby="projekt-anlage-ohne-recht" className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
                    <div className="bg-slate-50 p-6 border-b border-slate-100 flex justify-between items-center">
                        <h2 id="projekt-anlage-ohne-recht" className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <Building2 className="w-6 h-6 text-blue-600" />
                            Neues Projekt anlegen
                        </h2>
                        <button onClick={onCancel} title="Schließen" className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="flex items-start gap-3 text-sm text-slate-700">
                            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                            <p>
                                Sie dürfen keine Projekte anlegen. Dafür braucht es eines der Rechte
                                „SiGeKo-Projekt anlegen“, „SiFa-Betreuung anlegen“ oder „HSE-Projekt anlegen“ —
                                ein Administrator vergibt sie über die Rollen.
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <Button variant="secondary" onClick={onCancel}>Schließen</Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-glass-view="projekt-dialog" className="fixed inset-0 z-[150] bg-slate-900/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className={`bg-white rounded-2xl shadow-2xl w-full ${step === 2 ? 'max-w-6xl' : 'max-w-5xl'} overflow-hidden flex flex-col animate-scale-in transition-all duration-300 h-[90vh]`}>
                <div className="bg-slate-50 p-6 border-b border-slate-100 flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <Building2 className="w-6 h-6 text-blue-600" />
                            Neues Projekt anlegen
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">Schritt {step} von 2: {step === 1 ? 'Stammdaten' : 'Leistungsverzeichnis & Aufgaben'}</p>
                    </div>
                    <button onClick={onCancel} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8">
                    {step === 1 ? (
                        <div className="space-y-6 max-w-2xl mx-auto">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Projektname *</label>
                                <input 
                                    data-tutorial="projekt.name"
                                    type="text" 
                                    className="w-full px-4 py-3 text-lg font-bold border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="z.B. Neubau Bürokomplex Hafen"
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Kategorie & Unterkategorie Selection — bei SiFa ausgeblendet
                                (ein Betrieb ist keine Baustelle; die Werte bleiben leer). */}
                            {!isSifa && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                                        <LayoutTemplate className="w-3.5 h-3.5 text-blue-600" /> Kategorie
                                    </label>
                                    <div className="relative">
                                        <select 
                                            value={projectCategory} 
                                            onChange={(e) => {
                                                const cat = e.target.value;
                                                setProjectCategory(cat);
                                                const subs = projectCategories[cat] || [];
                                                setProjectSubcategory(subs.length > 0 ? subs[0] : '');
                                            }} 
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium appearance-none shadow-sm"
                                        >
                                            <option value="">-- Keine Kategorie --</option>
                                            {Object.keys(projectCategories).map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                                        <LayoutTemplate className="w-3.5 h-3.5 text-blue-600" /> Unterkategorie
                                    </label>
                                    <div className="relative">
                                        <select 
                                            value={projectSubcategory} 
                                            onChange={(e) => setProjectSubcategory(e.target.value)}
                                            disabled={!projectCategory}
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium appearance-none shadow-sm disabled:bg-slate-100 disabled:text-slate-400"
                                        >
                                            <option value="">-- Keine Unterkategorie --</option>
                                            {projectCategory && (projectCategories[projectCategory] || []).map(sub => (
                                                <option key={sub} value={sub}>{sub}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>}

                            {/* Die Art steuerte bisher nur die Auto-Nummer und war bei
                                manueller Nummer verborgen. Seit sie den Projekttyp
                                (metadata.projectType) setzt, steht sie immer da —
                                mit den Typen, die man anlegen darf. */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Art der Dienstleistung{!isManualNumber ? ' (Auto-Nummerierung)' : ''}</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {erlaubteArten.map((t, typIndex) => (
                                        <button
                                            key={t}
                                            {...anker('projekt.typ', typIndex === 0)}
                                            onClick={() => setSelectedProjectType(t)}
                                            className={`py-2 px-3 rounded-lg border text-sm font-bold capitalize transition-all ${selectedProjectType === t ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                        >
                                            {t === 'sigeko' ? 'SiGeKo' : t === 'sifa' ? 'SiFa' : t === 'hse' ? 'HSE' : t === 'digital' ? 'Digital' : 'Sonstiges'}
                                        </button>
                                    ))}
                                </div>
                                {isSifa && (
                                    <p className="text-[11px] text-teal-700 mt-2">
                                        Fachkraft für Arbeitssicherheit: unbefristete Betreuung — das Enddatum darf leer bleiben, ein Begehungsintervall wird nicht vorbelegt.
                                    </p>
                                )}
                            </div>

                            {/* Betrieb (SiFa): die drei Angaben, an denen Betreuungsgruppe,
                                Einsatzzeit-Soll und ASA-Pflicht haengen. Alles Weitere
                                (Bestellung, Betriebsarzt, Ersthelfer …) in den Stammdaten. */}
                            {isSifa && (
                                <div className="bg-teal-50/60 p-4 rounded-xl border border-teal-100 space-y-3">
                                    <span className="text-xs font-bold text-teal-800 uppercase">Betrieb</span>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Beschäftigte</label>
                                            <input type="number" min={0} step={1} value={sifaBeschaeftigte} onChange={(e) => setSifaBeschaeftigte(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" placeholder="Kopfzahl" />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Betreuungsgruppe</label>
                                            <select value={sifaGruppe} onChange={(e) => setSifaGruppe(e.target.value as SifaBetreuungsgruppe | '')} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                                                <option value="">-- offen --</option>
                                                {BETREUUNGSGRUPPEN.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Berufsgenossenschaft</label>
                                            <input type="text" value={sifaBg} onChange={(e) => setSifaBg(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="z. B. BGHM" />
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-slate-500">{BETREUUNGSGRUPPEN.find(g => g.id === sifaGruppe)?.hilfe || 'DGUV Vorschrift 2, Anlage 2: I = 2,5 h · II = 1,5 h · III = 0,5 h je Beschäftigtem und Jahr.'}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-xs font-bold text-slate-500 uppercase">Projektnummer</label>
                                        <label className="flex items-center gap-1 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={isManualNumber} 
                                                onChange={(e) => {
                                                    setIsManualNumber(e.target.checked);
                                                    setNumberStatus('idle');
                                                    if (!e.target.checked) generateProjectNumber();
                                                    else setProjectNumber('');
                                                }}
                                                className="rounded text-blue-600 focus:ring-blue-500 w-3 h-3"
                                            />
                                            <span className="text-[11px] text-blue-600 font-bold uppercase">Manuell</span>
                                        </label>
                                    </div>
                                    <div className="relative">
                                        <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input 
                                            data-tutorial="projekt.nummer"
                                            type="text" 
                                            className={`w-full pl-10 pr-8 py-2 border rounded-lg outline-none font-mono transition-all ${
                                                numberStatus === 'taken' ? 'border-red-500 focus:ring-red-500 bg-red-50' :
                                                numberStatus === 'available' ? 'border-green-500 focus:ring-green-500 bg-green-50' :
                                                'border-slate-300 focus:ring-blue-500'
                                            } ${!isManualNumber ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-white'}`}
                                            placeholder={isManualNumber ? "Eigene Nr. eingeben" : "Auto-Generiert"}
                                            value={projectNumber}
                                            readOnly={!isManualNumber}
                                            onChange={(e) => {
                                                setProjectNumber(e.target.value);
                                                if(isManualNumber) setNumberStatus('idle');
                                            }}
                                            onBlur={() => isManualNumber && checkNumberAvailability(projectNumber)}
                                        />
                                        {!isManualNumber && (
                                            <button onClick={generateProjectNumber} disabled={isGeneratingNumber} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600" title="Neu generieren">
                                                <RefreshCw className={`w-4 h-4 ${isGeneratingNumber ? 'animate-spin' : ''}`} />
                                            </button>
                                        )}
                                        {isManualNumber && numberStatus === 'checking' && (
                                            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                                        )}
                                        {isManualNumber && numberStatus === 'available' && (
                                            <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
                                        )}
                                        {isManualNumber && numberStatus === 'taken' && (
                                            <XCircle className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-red-600" />
                                        )}
                                    </div>
                                    {isManualNumber && numberStatus === 'taken' && <p className="text-[11px] text-red-600 mt-1 font-bold">Nummer bereits vergeben!</p>}
                                    {!isManualNumber && <p className="text-[10px] text-slate-400 mt-1">Automatisch generiert: JJMM + Typ + Lfd. Nr.</p>}
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase">Standort</label>
                                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                                        <div className="grid grid-cols-3 gap-2 mb-2">
                                            <div className="col-span-2">
                                                <input 
                                                    data-tutorial="projekt.standort"
                                                    type="text"
                                                    value={addrStreet}
                                                    onChange={e => setAddrStreet(e.target.value)}
                                                    onBlur={handleGeocode}
                                                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-xs"
                                                    placeholder="Straße & Nr."
                                                />
                                            </div>
                                            <div>
                                                <input 
                                                    type="text"
                                                    value={addrZip}
                                                    onChange={e => setAddrZip(e.target.value)}
                                                    onBlur={handleGeocode}
                                                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-xs"
                                                    placeholder="PLZ"
                                                />
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <input 
                                                type="text"
                                                value={addrCity}
                                                onChange={e => setAddrCity(e.target.value)}
                                                onBlur={handleGeocode}
                                                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-xs"
                                                placeholder="Ort / Stadt"
                                            />
                                            {isGeocoding ? (
                                                <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-blue-500" />
                                            ) : addrCoordinates ? (
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2" title="Koordinaten gefunden">
                                                    <MapPin className="w-3 h-3 text-green-500" />
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <NearbyProjectsPanel
                                checking={nearby.checking}
                                projects={nearby.projects}
                                geocodeFailed={nearby.geocodeFailed}
                            />

                            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{isSifa ? 'Betreuungsbeginn' : 'Geplanter Start'}</label>
                                        <input
                                            type="date"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                            value={plannedStart}
                                            min={isSifa ? undefined : new Date().toISOString().split('T')[0]}
                                            onChange={(e) => setPlannedStart(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{isSifa ? 'Ende (leer = unbefristet)' : 'Vorauss. Ende'}</label>
                                        <input 
                                            type="date" 
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                            value={plannedEnd}
                                            min={plannedStart}
                                            onChange={(e) => {
                                                setPlannedEnd(e.target.value);
                                                setIsManualEnd(true);
                                            }}
                                        />
                                    </div>
                                </div>
                                
                                {!isSifa && <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Begehungsintervall</label>
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            { id: 'weekly', label: 'Wöchentlich' },
                                            { id: 'biweekly', label: '14-tägig' },
                                            { id: 'twice_monthly', label: '2x Monat' },
                                            { id: 'monthly', label: 'Monatlich' }
                                        ].map((opt, intervallIndex) => (
                                            <button 
                                                key={opt.id}
                                                {...anker('projekt.intervall', intervallIndex === 0)}
                                                onClick={() => setIntervalType(opt.id as IntervalType)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${intervalType === opt.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                        <div className={`flex items-center rounded-lg border bg-white px-2 py-1 ${intervalType === 'custom' ? 'border-blue-600 ring-1 ring-blue-200' : 'border-slate-200'}`}>
                                            <button onClick={() => setIntervalType('custom')} className="text-xs font-medium text-slate-600 mr-2">Tage:</button>
                                            <input 
                                                type="number" 
                                                min="1" 
                                                value={customDays}
                                                onChange={(e) => {
                                                    setCustomDays(parseInt(e.target.value) || 1);
                                                    setIntervalType('custom');
                                                }}
                                                className="w-10 text-xs text-center outline-none font-bold"
                                            />
                                        </div>
                                    </div>
                                </div>}
                            </div>

                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-4">
                                {/* Bauherr nur bei Baustellen — ein betreuter Betrieb hat keinen. */}
                                {!isSifa && <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <User className="w-4 h-4 text-blue-600"/>
                                        <span className="text-xs font-bold text-slate-700 uppercase">Bauherr</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                        <input data-tutorial="projekt.bauherr" type="text" placeholder="Firma / Name" value={clientName} onChange={(e) => setClientName(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                        <input type="text" placeholder="Anschrift" value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <input type="text" placeholder="Ansprechpartner" value={clientContact} onChange={(e) => setClientContact(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                        <input type="text" placeholder="Telefon" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                        <input type="text" placeholder="E-Mail" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                    </div>
                                </div>}

                                {crmCustomers.length > 0 && (
                                    <div className={isSifa ? '' : 'border-t border-slate-200 pt-3'}>
                                        <div className="bg-white p-3 rounded-lg border border-blue-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Database className="w-4 h-4 text-blue-600"/>
                                                <span className="text-xs font-bold text-slate-700 uppercase">Kunde aus CRM übernehmen</span>
                                            </div>
                                            <CustomerPicker
                                                customers={crmCustomers}
                                                value={selectedCrmCustomerId || undefined}
                                                onSelect={handleSelectCrmCustomer}
                                                placeholder="CRM-Kunde auswählen…"
                                            />
                                            {selectedCrmCustomerId && (
                                                <div className="mt-2 inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700">
                                                    <Link2 className="w-3 h-3" />
                                                    <span className="font-medium">
                                                        Verknüpft mit CRM-Kunde: {crmCustomers.find((c) => c.id === selectedCrmCustomerId)?.name || 'Unbekannt'}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={handleUnlinkCrmCustomer}
                                                        title="Verknüpfung lösen"
                                                        className="p-0.5 rounded-full hover:bg-blue-200 text-blue-600 transition-colors"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className={isSifa && crmCustomers.length === 0 ? '' : 'border-t border-slate-200 pt-3'}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Building2 className="w-4 h-4 text-slate-500"/>
                                        <span className="text-xs font-bold text-slate-700 uppercase">{isSifa ? 'Unternehmen/Auftraggeber' : 'Auftraggeber'}</span>
                                        {!isSifa && <button onClick={() => { setCustomerName(clientName); setCustomerAddress(clientAddress); setCustomerContact(clientContact); setCustomerPhone(clientPhone); setCustomerEmail(clientEmail); }} className="text-[11px] text-blue-600 hover:underline ml-2">Wie Bauherr</button>}
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-2">
                                        <input type="text" placeholder="Firma / Name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                        <input type="text" placeholder="Anschrift" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <input type="text" placeholder="Ansprechpartner" value={customerContact} onChange={(e) => setCustomerContact(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                        <input type="text" placeholder="Telefon" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                        <input type="text" placeholder="E-Mail" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                                    </div>
                                </div>
                            </div>

                            {darfZuweisen && (
                                <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                                    <label className="text-xs font-bold text-indigo-800 uppercase mb-2 flex items-center gap-1">
                                        <UserCheck className="w-3 h-3"/> Hauptverantwortlicher (Ausführung, Projektbesitzer) *
                                    </label>
                                    <select
                                        value={assignedUserId}
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            setAssignedUserId(id);
                                            // Wer Hauptverantwortlicher wird, plant ohnehin
                                            // mit — als Planungsperson stünde er doppelt da.
                                            setPlanningUserIds(ids => ids.filter(x => x !== id));
                                        }}
                                        className={`w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm ${assignedUserId ? 'border-indigo-200' : 'border-amber-300'}`}
                                    >
                                        <option value="">Bitte auswählen …</option>
                                        {ohneGesperrte(availableUsers, gesperrteKonten, assignedUserId).map(u => {
                                            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                                            const km = coordinatorDistanceKm(nearby.projects, name);
                                            return (
                                                <option key={u.id} value={u.id}>
                                                    {u.first_name} {u.last_name} ({u.email})
                                                    {km !== null ? ` · Projekt in ca. ${formatDistanceKm(km)} Entfernung` : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    {!assignedUserId && (
                                        <p className="text-[11px] text-amber-700 mt-1 font-bold">
                                            Pflichtfeld — bitte aktiv auswählen, es gibt keine Vorbelegung.
                                        </p>
                                    )}
                                    {/* Verantwortliche Planungsphase: SiFa kennt keine
                                        Planungsphase, deshalb dort ausgeblendet
                                        (handleFinish setzt nie). Mehrere Personen
                                        möglich; leer = der Hauptverantwortliche plant
                                        selbst. Der Hauptverantwortliche selbst steht
                                        nicht zur Wahl. */}
                                    {!isSifa && (() => {
                                        const gewaehlt = planningUserIds
                                            .filter(id => id !== assignedUserId)
                                            .map(id => availableUsers.find(u => u.id === id))
                                            .filter((u): u is UserProfile => !!u);
                                        const waehlbar = ohneGesperrte(availableUsers, gesperrteKonten)
                                            .filter(u => u.id !== assignedUserId && !planningUserIds.includes(u.id));
                                        return (
                                            <div className="mt-3">
                                                <label className="text-xs font-bold text-indigo-800 uppercase mb-2 flex items-center gap-1">
                                                    <UserCheck className="w-3 h-3"/> Verantwortliche Planungsphase
                                                </label>
                                                {gewaehlt.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                                        {gewaehlt.map(u => (
                                                            <span key={u.id} className="inline-flex items-center gap-1 bg-white border border-indigo-200 text-indigo-800 text-xs font-bold rounded-full pl-2.5 pr-1 py-0.5">
                                                                {profilName(u)}
                                                                <button
                                                                    type="button"
                                                                    title="Entfernen"
                                                                    onClick={() => setPlanningUserIds(ids => ids.filter(x => x !== u.id))}
                                                                    className="p-0.5 rounded-full text-indigo-400 hover:text-red-600 hover:bg-red-50"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <select
                                                    value=""
                                                    onChange={(e) => {
                                                        const id = e.target.value;
                                                        if (id) setPlanningUserIds(ids => (ids.includes(id) ? ids : [...ids, id]));
                                                    }}
                                                    disabled={waehlbar.length === 0}
                                                    className="w-full px-3 py-2 border border-indigo-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm disabled:bg-slate-50 disabled:text-slate-400"
                                                >
                                                    <option value="">+ Person hinzufügen …</option>
                                                    {waehlbar.map(u => (
                                                        <option key={u.id} value={u.id}>
                                                            {u.first_name} {u.last_name} ({u.email})
                                                        </option>
                                                    ))}
                                                </select>
                                                <p className="text-[11px] text-indigo-700/80 mt-1">
                                                    {gewaehlt.length === 0
                                                        ? 'Leer = der Hauptverantwortliche plant selbst. '
                                                        : 'Die Planungspersonen kommen als eigene Rolle in die Verantwortlichkeiten und bekommen die Planungstermine. '}
                                                    Planung = Vorankündigung, Unterlage, SiGe-Plan, A+S-Plan, Planung allg. — keine Begehungen.
                                                </p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col h-full">
                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-blue-800 text-sm flex justify-between items-center mb-6 shrink-0">
                                <div>
                                    <span className="block font-bold">Leistungsverzeichnis (LV) erstellen</span>
                                    <span className="text-xs opacity-80">Aufgaben und Termine werden daraus automatisch generiert.</span>
                                </div>
                                
                                <div className="flex gap-2">
                                    <Button 
                                        variant="secondary" 
                                        onClick={handleSaveAsTemplate} 
                                        disabled={selectedServices.length === 0 || isSavingTemplate}
                                        className="text-xs py-1.5 h-auto bg-white border-blue-200 text-blue-700 hover:bg-blue-50"
                                        icon={isSavingTemplate ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}
                                    >
                                        Als Vorlage speichern
                                    </Button>

                                    <Button variant="ai" onClick={() => lvFileInputRef.current?.click()} disabled={isAiLoading} className="text-xs py-1.5 h-auto">
                                        {isAiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                                        LV per KI einlesen
                                    </Button>
                                    <input type="file" ref={lvFileInputRef} accept="application/pdf,image/*" className="hidden" onChange={handleLvUpload} />
                                </div>
                            </div>
                            
                            {suggestedEndDate && suggestedEndDate !== plannedEnd && (
                                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-sm text-amber-800 flex justify-between items-center mb-4 animate-in fade-in slide-in-from-top-2">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>
                                            Berechnetes Enddatum ({new Date(suggestedEndDate).toLocaleDateString()}) weicht vom manuellen Datum ab.
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => { setPlannedEnd(suggestedEndDate); setSuggestedEndDate(null); setIsManualEnd(false); }}
                                        className="text-xs font-bold bg-white border border-amber-300 px-3 py-1 rounded hover:bg-amber-100"
                                    >
                                        Übernehmen
                                    </button>
                                </div>
                            )}

                            {/* Pauschal-Pruefung: errors sperren "Projekt erstellen"
                                (handleFinish), warnings informieren nur. */}
                            {flatIssues.length > 0 && (
                                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-sm text-amber-800 space-y-1 mb-4">
                                    {flatIssues.map((issue, idx) => (
                                        <div key={idx} className="flex items-start gap-2">
                                            <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${issue.level === 'error' ? 'text-red-600' : 'text-amber-500'}`} />
                                            <span className={issue.level === 'error' ? 'font-bold text-red-700' : ''}>{issue.message}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Bestandteile, die nicht zur Position passen, sperren
                                „Projekt erstellen" (handleFinish). */}
                            {bestandteilFehler.length > 0 && (
                                <div className="bg-red-50 p-3 rounded-lg border border-red-200 text-sm text-red-800 space-y-1 mb-4">
                                    {bestandteilFehler.map(f => (
                                        <div key={f.serviceId} className="flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
                                            <span><span className="font-semibold">{f.titel}:</span> {f.text}{!isAdmin ? ' Bitte die Menge auf 1 setzen oder einen Admin die Bestandteile entfernen lassen.' : ''}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Rahmenvertrag des Kunden: die vereinbarten Preise
                                liegen beim Kunden, nicht im Katalog — ohne diesen
                                Hinweis rechnete das LV still mit Listenpreisen. */}
                            {selectedCrmCustomer && frameworkSources.length > 0 && (() => {
                                const primary = frameworkSources[0];
                                const more = frameworkSources.length - 1;
                                const active = primary.validity === 'active';
                                return (
                                    <div className={`p-3 rounded-lg border text-sm mb-4 shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${active ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                                        <div className="flex items-start gap-2 min-w-0">
                                            <Link2 className="w-4 h-4 mt-0.5 shrink-0" />
                                            <div className="min-w-0">
                                                <span>
                                                    Kunde <b>{selectedCrmCustomer.name}</b> hat Rahmenvertrag <b>{frameworkContractLabel(primary.contract)}</b> mit {primary.items.length} Preispositionen ({FRAMEWORK_VALIDITY_LABELS[primary.validity]}).
                                                </span>
                                                {more > 0 && (
                                                    <span className="block text-xs opacity-80 mt-0.5">
                                                        {more === 1 ? 'Ein weiterer Rahmenvertrag ist' : `${more} weitere Rahmenverträge sind`} im Katalog unter „Ansicht" wählbar.
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => openCatalogWithSource(primary.key)}
                                            className={`shrink-0 text-xs font-bold bg-white border px-3 py-1.5 rounded-lg transition-colors ${active ? 'border-emerald-300 text-emerald-800 hover:bg-emerald-100' : 'border-amber-300 text-amber-800 hover:bg-amber-100'}`}
                                        >
                                            Positionen aus Rahmenvertrag wählen
                                        </button>
                                    </div>
                                );
                            })()}

                            <div className="border border-slate-200 rounded-xl overflow-hidden flex-1 overflow-y-auto mb-4">
                                <table className="w-full text-left text-sm relative">
                                    <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th className="p-3 w-16">Pos.</th>
                                            <th className="p-3">Leistung</th>
                                            <th className="p-3 w-20 text-center">Menge</th>
                                            <th className="p-3 w-20">Einheit</th>
                                            <th className="p-3 w-24 text-center" title="Mindeststunden je Buchung (nur Stundenpositionen)">Mind. Std.</th>
                                            <th className="p-3 w-24 text-right">EP (€)</th>
                                            <th className="p-3 w-32">Fällig am</th>
                                            <th className="p-3 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {selectedServices.length === 0 ? (
                                            <tr><td colSpan={8} className="p-8 text-center text-slate-400 italic">Keine Leistungen ausgewählt.</td></tr>
                                        ) : selectedServices.map((item, lvIndex) => {
                                            // PAKETPOSITION (E6): festlegen nur Admins; alle
                                            // anderen sehen eine aus der Vorlage kopierte Liste
                                            // nur lesend. Auf einer Monatspauschale gibt es
                                            // keine — der Schalter erscheint dort nur, wenn
                                            // (faelschlich) schon welche dranstehen.
                                            const mitBestandteilen = hatBestandteile(item);
                                            const zeigeBestandteile = mitBestandteilen || (isAdmin && item.assignment !== 'flatrate' && !item.flatRate);
                                            const bestandteileAuf = offeneBestandteile.has(item.id);
                                            const bestandteilFehlerZeile = bestandteilFehler.find(f => f.serviceId === String(item.id));
                                            return (
                                            <React.Fragment key={item.id}>
                                            <tr {...anker('projekt.lv-position', lvIndex === 0)} className="hover:bg-slate-50 group">
                                                <td className="p-3 font-mono text-xs text-slate-500">
                                                    <div>{item.lvPosition}</div>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Kunden-Pos." 
                                                        value={item.clientLvPosition || ''}
                                                        onChange={(e) => updateService(item.id, 'clientLvPosition', e.target.value)}
                                                        className="w-full mt-1 text-[11px] border border-slate-200 rounded px-1 py-0.5 outline-none focus:border-indigo-300"
                                                    />
                                                </td>
                                                <td className="p-3">
                                                    <input 
                                                        type="text" 
                                                        value={item.title} 
                                                        onChange={(e) => updateService(item.id, 'title', e.target.value)}
                                                        className="w-full bg-transparent outline-none font-medium text-slate-800"
                                                    />
                                                    {item.assignment && item.assignment !== 'none' && <div className="text-[10px] text-indigo-500 font-medium mt-1">Funktion: {item.assignment}</div>}
                                                    {zeigeBestandteile && (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleBestandteile(item.id)}
                                                            aria-expanded={bestandteileAuf}
                                                            className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold py-0.5 ${bestandteilFehlerZeile ? 'text-red-700' : mitBestandteilen ? 'text-indigo-700 hover:text-indigo-900' : 'text-slate-400 hover:text-indigo-700'}`}
                                                        >
                                                            <ListChecks className="w-3.5 h-3.5" />
                                                            {mitBestandteilen ? `Bestandteile (${(item.bestandteile || []).length})` : 'Bestandteile festlegen'}
                                                        </button>
                                                    )}
                                                    {/* MONATSPAUSCHALE (Betreiber-Entscheidung: die Einheit
                                                        'Monat' allein erzeugt KEINE Deckung — erst die
                                                        ausdrueckliche Auswahl hier, flatRateSetup). */}
                                                    {(isMonthlyUnit(item.unit) || item.assignment === 'flatrate' || item.flatRate) && (
                                                        <div className="mt-2 bg-amber-50/60 border border-amber-200 rounded-lg p-2 space-y-1.5">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[10px] font-bold text-amber-700 uppercase shrink-0">Pauschale deckt</span>
                                                                <select
                                                                    value={flatScopeChoiceOf(item)}
                                                                    onChange={(e) => patchService(item.id, applyFlatScopeChoice(item, e.target.value as FlatScopeChoice))}
                                                                    className={`flex-1 border rounded px-2 py-1 text-xs font-medium outline-none ${flatScopeChoiceOf(item) ? 'bg-white border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-500'}`}
                                                                >
                                                                    <option value="">Keine Pauschale</option>
                                                                    {PROJECT_FLAT_SCOPES.map(scope => <option key={scope} value={scope}>{FLAT_SCOPE_LABELS[scope]}</option>)}
                                                                </select>
                                                            </div>
                                                            {item.flatRate?.scope === 'explicit' && (
                                                                <div className="space-y-0.5 max-h-28 overflow-y-auto">
                                                                    {selectedServices.filter(s => s.id !== item.id && s.assignment !== 'flatrate' && !s.flatRate).length === 0 ? (
                                                                        <div className="text-[10px] text-amber-700 italic">Keine anderen Positionen im LV.</div>
                                                                    ) : selectedServices.filter(s => s.id !== item.id && s.assignment !== 'flatrate' && !s.flatRate).map(s => (
                                                                        <label key={s.id} className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={(item.flatRate?.serviceIds || []).includes(s.id)}
                                                                                onChange={() => patchService(item.id, toggleExplicitServiceId(item, s.id))}
                                                                                className="rounded text-amber-600 focus:ring-amber-500 w-3 h-3"
                                                                            />
                                                                            <span className="font-mono text-[10px] text-slate-400">{s.lvPosition}</span>
                                                                            <span className="truncate">{s.title}</span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-3">
                                                    <input 
                                                        type="number" 
                                                        value={item.quantityContracted} 
                                                        onChange={(e) => updateService(item.id, 'quantityContracted', parseFloat(e.target.value) || 0)}
                                                        className="w-full text-center border rounded bg-white px-2 py-1 focus:border-blue-500 outline-none"
                                                    />
                                                    {/* Zahl der beauftragten Monate; 0 = Laufzeit offen. */}
                                                    {item.assignment === 'flatrate' && (
                                                        <div className="text-[10px] text-amber-700 mt-1 leading-tight text-center">Monate · 0 = offen</div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-slate-500 text-xs">{item.unit}</td>
                                                <td className="p-3 text-center">
                                                    {isHourlyUnit(item.unit) ? (
                                                        <>
                                                            {/* NICHT type="number": ein Zahlenfeld leert
                                                                e.target.value fuer jeden Zwischenstand, der keine
                                                                gueltige Gleitkommazahl ist ("2.", "-", "0,"). Der
                                                                Handler bekaeme "" und koennte das nicht von einem
                                                                geleerten Feld unterscheiden — eine vereinbarte
                                                                Vorgabe waere nach einem Backspace geloescht. */}
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                placeholder="1"
                                                                value={minHoursText[item.id] ?? (item.minHours ?? '')}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    setMinHoursText(prev => ({ ...prev, [item.id]: raw }));
                                                                    // Zwischenstufen ("0", "2.") lassen den Wert stehen;
                                                                    // nur ein leeres Feld nimmt die Vorgabe zurueck.
                                                                    const next = applyMinHoursInput(raw, item.minHours);
                                                                    if (next !== item.minHours) updateService(item.id, 'minHours', next);
                                                                }}
                                                                className={`w-full text-center border rounded px-2 py-1 outline-none ${item.minHours === undefined ? 'border-amber-300 bg-amber-50 focus:border-amber-500' : 'border-slate-200 bg-white focus:border-blue-500'}`}
                                                                title="Mindeststunden, die bei einer Buchung angesetzt werden"
                                                            />
                                                            {item.minHours === undefined && (
                                                                <div className="text-[10px] font-bold text-amber-700 mt-1 leading-tight">
                                                                    Mindeststunden festlegen
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-slate-300">–</span>
                                                    )}
                                                </td>
                                                <td className="p-3">
                                                    <input
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={item.unitPrice || ''}
                                                        onChange={(e) => updateService(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                        className="w-full text-right border rounded bg-white px-2 py-1 focus:border-blue-500 outline-none"
                                                    />
                                                </td>
                                                <td className="p-3">
                                                    <input 
                                                        type="date" 
                                                        value={item.dueDate || ''}
                                                        onChange={(e) => updateService(item.id, 'dueDate', e.target.value)}
                                                        className={`w-full text-xs bg-transparent outline-none ${item.dueDate && plannedStart && item.dueDate < plannedStart ? 'text-orange-600 font-bold' : 'text-slate-600'}`}
                                                    />
                                                </td>
                                                <td className="p-3 text-right">
                                                    <button onClick={() => removeService(item.id)} className="text-slate-300 hover:text-red-500 p-1">
                                                        <Trash2 className="w-4 h-4"/>
                                                    </button>
                                                </td>
                                            </tr>
                                            {zeigeBestandteile && bestandteileAuf && (() => {
                                                // Menge ≠ 1 ohne Bestandteile: nur der Hinweis.
                                                // MIT Bestandteilen der Editor samt Fehler aus
                                                // pruefeDefinition — die Anlage ist dann gesperrt.
                                                const sperreGrund = positionsSperreFuerBestandteile(item);
                                                return (
                                                    <tr className="bg-slate-50/60">
                                                        <td colSpan={8} className="px-3 pb-3 pt-1">
                                                            {!mitBestandteilen && sperreGrund ? (
                                                                <p className="text-[11px] text-slate-500">{sperreGrund}</p>
                                                            ) : (
                                                                <div className="space-y-3">
                                                                    <BestandteileEditor
                                                                        wert={item.bestandteile}
                                                                        // Neue Position: „keine" heisst kein Schluessel —
                                                                        // und ohne Bestandteile kein Kontingent (S1).
                                                                        onChange={(neu) => patchService(item.id, bestandteilePatch(neu))}
                                                                        vorlage={catalogItems.find(c => c.lvPosition === item.lvPosition)?.bestandteile}
                                                                        disabled={!isAdmin}
                                                                        service={item}
                                                                    />
                                                                    {/* STUNDENKONTINGENT (intern): aus dem Katalog
                                                                        kopiert; aendern nur Admins (S1). */}
                                                                    {mitBestandteilen && (
                                                                        <div className="max-w-xs">
                                                                            <StundenKontingentFeld
                                                                                wert={item.stundenKontingent}
                                                                                onChange={(k) => patchService(item.id, { stundenKontingent: k })}
                                                                                deaktiviert={!isAdmin}
                                                                                sperrHinweis="Festlegen nur Admins."
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })()}
                                            </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="bg-slate-50 border-t border-slate-200 sticky bottom-0 z-10 font-bold">
                                        <tr>
                                            <td colSpan={5} className="p-3 text-right text-slate-600">Summe (Netto):</td>
                                            <td className="p-3 text-right font-mono text-slate-800">{calculateTotal().toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <div className="shrink-0">
                                <button 
                                    onClick={() => { refreshCatalog(); setIsCatalogOpen(true); }}
                                    className="flex items-center gap-2 text-sm font-bold text-blue-600 hover:bg-blue-50 px-4 py-3 rounded-lg transition-colors border-2 border-blue-200 w-full justify-center"
                                >
                                    <Plus className="w-5 h-5"/> Position aus Katalog hinzufügen
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
                    {step === 2 ? (
                        <button onClick={() => setStep(1)} className="text-slate-500 hover:text-slate-800 text-sm font-bold px-4 py-2">
                            Zurück
                        </button>
                    ) : (
                        <div></div>
                    )}
                    
                    <Button
                        data-tutorial="projekt.wizard-weiter"
                        variant="primary"
                        onClick={step === 1 ? handleNext : handleFinish}
                        disabled={(darfZuweisen && !assignedUserId) || (step === 2 && bestandteilFehler.length > 0)}
                        title={darfZuweisen && !assignedUserId
                            ? 'Bitte zuerst einen Hauptverantwortlichen (Ausführung, Projektbesitzer) auswählen.'
                            : step === 2 && bestandteilFehler.length > 0
                                ? 'Bestandteile passen nicht zur Position — siehe Hinweis über der Tabelle.'
                                : undefined}
                        className="px-6 py-3 h-auto text-base shadow-lg"
                        icon={step === 1 ? <ArrowRight className="w-5 h-5" /> : <CheckSquare className="w-5 h-5" />}
                    >
                        {step === 1 ? 'Weiter zum LV' : 'Projekt erstellen'}
                    </Button>
                </div>
            </div>

            {isCatalogOpen && (
                <div className="fixed inset-0 z-[250] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                        
                        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                            <div>
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <Database className="w-5 h-5 text-blue-400" /> 
                                    Leistungskatalog
                                </h3>
                                <p className="text-xs text-slate-400">Wählen Sie Positionen für Ihr Projekt aus oder erstellen Sie neue.</p>
                            </div>
                            <button onClick={() => setIsCatalogOpen(false)} className="hover:bg-white/10 p-2 rounded-full transition-colors"><X className="w-5 h-5"/></button>
                        </div>

                        <div className="flex flex-1 overflow-hidden">
                            <div className="flex-1 flex flex-col border-r border-slate-200">
                                <div className="p-4 border-b border-slate-100 bg-slate-50 space-y-3">
                                    {(templates.length > 0 || frameworkSources.length > 0) && (
                                        <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-blue-200 shadow-sm">
                                            <LayoutTemplate className="w-4 h-4 text-blue-500" />
                                            <select
                                                value={selectedTemplateId}
                                                onChange={(e) => handleLoadTemplate(e.target.value)}
                                                className="text-xs w-full bg-transparent outline-none font-bold text-slate-700 cursor-pointer"
                                            >
                                                <option value="">-- Ansicht: Gesamt-Katalog --</option>
                                                {frameworkSources.map(s => (
                                                    <option key={s.key} value={s.key}>
                                                        Rahmenvertrag: {frameworkContractLabel(s.contract)}{s.validity !== 'active' ? ` (${FRAMEWORK_VALIDITY_LABELS[s.validity]})` : ''}
                                                    </option>
                                                ))}
                                                {templates.map(t => (
                                                    <option key={t.id} value={t.id}>Vorlage: {t.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {/* Vertragsquelle: Preise sind die vereinbarten Nettopreise
                                        (Positions- und Gesamtrabatt schon eingerechnet). */}
                                    {activeFrameworkSource && (
                                        <p className={`text-[11px] px-1 ${activeFrameworkSource.validity === 'active' ? 'text-slate-500' : 'text-amber-700 font-bold'}`}>
                                            {activeFrameworkSource.validity === 'active'
                                                ? 'Preise = vereinbarte Nettopreise des Rahmenvertrags (Rabatte eingerechnet).'
                                                : `Rahmenvertrag ${FRAMEWORK_VALIDITY_LABELS[activeFrameworkSource.validity]} — Preise vor der Übernahme prüfen.`}
                                        </p>
                                    )}

                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input 
                                                type="text" 
                                                placeholder="Suchen nach Titel oder Position..." 
                                                value={catalogSearch}
                                                onChange={(e) => setCatalogSearch(e.target.value)}
                                                className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                autoFocus
                                            />
                                        </div>
                                        <button onClick={refreshCatalog} className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-500" title="Aktualisieren">
                                            <RefreshCw className={`w-4 h-4 ${isCatalogLoading ? 'animate-spin' : ''}`} />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                    {/* Quelle (Katalog, Vorlage oder Rahmenvertrag) aus activeSourceItems, gefiltert nach Suche */}
                                    {(() => {
                                        const filtered = filteredSourceItems;

                                        if (filtered.length === 0) return <div className="p-8 text-center text-slate-400 text-sm italic">Keine Einträge gefunden.</div>;

                                        return filtered.map((item, idx) => {
                                            const isAdded = selectedServices.some(s => s.lvPosition === item.lvPosition);
                                            return (
                                                <div 
                                                    key={idx} 
                                                    className={`flex items-center justify-between p-3 rounded-lg border transition-all group ${isAdded ? 'bg-slate-50 border-slate-100 opacity-60' : 'hover:bg-slate-50 border-transparent hover:border-slate-200'}`}
                                                >
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 rounded">{item.lvPosition}</span>
                                                            <span className={`font-medium text-sm ${isAdded ? 'text-slate-500' : 'text-slate-700'}`}>{item.title}</span>
                                                        </div>
                                                        <div className="text-xs text-slate-400 mt-0.5 ml-1 flex items-center gap-2">
                                                            {/* Rahmenvertrags-Positionen haben nicht immer eine Kategorie. */}
                                                            {[item.category, item.unit, `${item.unitPrice} €`].filter(Boolean).join(' • ')}
                                                            {item.assignment && item.assignment !== 'none' && <span className="bg-indigo-50 text-indigo-600 px-1.5 rounded border border-indigo-100 font-bold">{item.assignment}</span>}
                                                            {item.is_optional && <span className="bg-amber-50 text-amber-700 px-1.5 rounded border border-amber-200 font-bold" title="Eventualposition: Ausführung nur auf gesonderte Anforderung">NEP</span>}
                                                        </div>
                                                    </div>
                                                    
                                                    {isAdded ? (
                                                        <span className="text-green-600 flex items-center gap-1 font-bold text-xs bg-green-50 px-2 py-1 rounded border border-green-100">
                                                            <CheckSquare className="w-3 h-3" /> Im LV
                                                        </span>
                                                    ) : (
                                                        <button 
                                                            onClick={() => addServiceFromCatalog(item)}
                                                            className="bg-white border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors shadow-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 focus:opacity-100"
                                                        >
                                                            Hinzufügen
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>

                            <div className="w-80 bg-slate-50 p-5 flex flex-col shrink-0 overflow-y-auto border-l border-slate-200">
                                <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-sm">
                                    <Plus className="w-4 h-4 text-blue-600" />
                                    Neue Position erstellen
                                </h4>
                                
                                <div className="space-y-3 flex-1">
                                    <div>
                                        <label className="text-[11px] uppercase font-bold text-slate-400 mb-1 block">Pos. Nr.</label>
                                        <input 
                                            type="text" 
                                            value={newItem.lvPosition} 
                                            onChange={(e) => setNewItem({...newItem, lvPosition: e.target.value})} 
                                            className="w-full p-2 border border-slate-200 rounded text-sm"
                                            placeholder="1.1.10"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[11px] uppercase font-bold text-slate-400 mb-1 block">Titel</label>
                                        <input 
                                            type="text" 
                                            value={newItem.title} 
                                            onChange={(e) => setNewItem({...newItem, title: e.target.value})} 
                                            className="w-full p-2 border border-slate-200 rounded text-sm"
                                            placeholder="Leistungsbezeichnung..."
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[11px] uppercase font-bold text-slate-400 mb-1 block">Preis</label>
                                            <input 
                                                type="number" 
                                                value={newItem.unitPrice} 
                                                onChange={(e) => setNewItem({...newItem, unitPrice: parseFloat(e.target.value) || 0})} 
                                                className="w-full p-2 border border-slate-200 rounded text-sm text-right"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[11px] uppercase font-bold text-slate-400 mb-1 block">Einheit</label>
                                            <select
                                                value={newItem.unit}
                                                onChange={(e) => setNewItem({...newItem, unit: e.target.value, ...(flatPatchForUnitChange(newItem, e.target.value) ?? {})})}
                                                className="w-full p-2 border border-slate-200 rounded text-sm bg-white"
                                            >
                                                <option value="Stück">Stück</option>
                                                <option value="Stunden">Stunden</option>
                                                <option value="Monat">Monat</option>
                                                <option value="Pauschal">Pauschal</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[11px] uppercase font-bold text-slate-400 mb-1 block">Kategorie</label>
                                        <select 
                                            value={newItem.category} 
                                            onChange={(e) => setNewItem({...newItem, category: e.target.value})} 
                                            className="w-full p-2 border border-slate-200 rounded text-sm bg-white"
                                        >
                                            <option value="Entwurf">Entwurf</option>
                                            <option value="Ausführung">Ausführung</option>
                                            <option value="Sonstiges">Sonstiges</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[11px] uppercase font-bold text-slate-400 mb-1 block">Funktion (System)</label>
                                        <select 
                                            value={newItem.assignment} 
                                            onChange={(e) => setNewItem({...newItem, assignment: e.target.value as ServiceAssignment})} 
                                            className="w-full p-2 border border-slate-200 rounded text-sm bg-white text-indigo-600 font-bold"
                                        >
                                            {assignmentOptions.map(opt => <option key={opt.id} value={opt.id} disabled={opt.id === 'flatrate'}>{opt.label}</option>)}
                                        </select>
                                    </div>

                                    {/* MONATSPAUSCHALE: nur bei Einheit 'Monat', und nur als
                                        ausdrueckliche Auswahl — ohne Umfang keine Deckung
                                        (flatRateSetup). 'einzelne Positionen' laesst sich nach
                                        dem Hinzufuegen in der LV-Tabelle waehlen. */}
                                    {isMonthlyUnit(newItem.unit) && (
                                        <div>
                                            <label className="text-[11px] uppercase font-bold text-amber-600 mb-1 block">Pauschale deckt</label>
                                            <select
                                                value={flatScopeChoiceOf(newItem)}
                                                onChange={(e) => setNewItem({ ...newItem, ...applyFlatScopeChoice(newItem, e.target.value as FlatScopeChoice) })}
                                                className={`w-full p-2 border rounded text-sm font-medium ${flatScopeChoiceOf(newItem) ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-500'}`}
                                            >
                                                <option value="">Keine Pauschale</option>
                                                {CATALOG_FLAT_SCOPES.map(scope => <option key={scope} value={scope}>{FLAT_SCOPE_LABELS[scope]}</option>)}
                                            </select>
                                        </div>
                                    )}

                                    {/* PAKETPOSITION: Bestandteil-Vorlage der neuen
                                        Katalogposition — nur fuer Admins (E6), denn nur
                                        sie schreiben in den Firmen-Katalog. */}
                                    {isAdmin && (
                                        <div className="rounded-lg border border-slate-200 bg-white p-2">
                                            <button
                                                type="button"
                                                onClick={() => setNeuBestandteileAuf(o => !o)}
                                                aria-expanded={neuBestandteileAuf}
                                                className="w-full flex items-center justify-between gap-2 text-[11px] font-semibold text-slate-600"
                                            >
                                                <span className="flex items-center gap-1.5">
                                                    <ListChecks className="w-3.5 h-3.5" />
                                                    Bestandteile (Vorlage){hatBestandteile(newItem) ? ` · ${(newItem.bestandteile || []).length}` : ''}
                                                </span>
                                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${neuBestandteileAuf ? 'rotate-180' : ''}`} />
                                            </button>
                                            {neuBestandteileAuf && (
                                                <div className="mt-2 space-y-3">
                                                    <BestandteileEditor
                                                        wert={newItem.bestandteile}
                                                        // Liste weg → Kontingent weg (S1).
                                                        onChange={(neu) => {
                                                            setNewItem(prev => ({ ...prev, ...bestandteilePatch(neu) }));
                                                            if (!neu) setNeuKontingentFehler(null);
                                                        }}
                                                        service={pruefPosition({ unit: newItem.unit, assignment: newItem.assignment, flatRate: newItem.flatRate })}
                                                    />
                                                    {/* STUNDENKONTINGENT der Vorlage — nur mit Bestandteilen (S1). */}
                                                    {hatBestandteile(newItem) && (
                                                        <StundenKontingentFeld
                                                            wert={newItem.stundenKontingent}
                                                            onChange={(k) => setNewItem(prev => ({ ...prev, stundenKontingent: k }))}
                                                            onFehler={setNeuKontingentFehler}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex items-center gap-2 pt-2">
                                        <input
                                            type="checkbox"
                                            id="isDefault"
                                            checked={newItem.isDefault} 
                                            onChange={(e) => setNewItem({...newItem, isDefault: e.target.checked})}
                                            className="rounded text-blue-600"
                                        />
                                        <label htmlFor="isDefault" className="text-xs text-slate-600">Standardmäßig ausgewählt</label>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-slate-200">
                                    <Button 
                                        onClick={handleCreateGlobalService} 
                                        disabled={!newItem.title || isCatalogLoading}
                                        className="w-full justify-center text-xs"
                                        icon={isCatalogLoading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Save className="w-3 h-3"/>}
                                    >
                                        Erstellen & Speichern
                                    </Button>
                                    {neuVorlageGeprueft && neuVorlagenFehler && (
                                        <p role="alert" className="text-[11px] font-semibold text-red-700 mt-2 text-center">
                                            Bestandteile: {neuVorlagenFehler}
                                        </p>
                                    )}
                                    {neuVorlageGeprueft && !neuVorlagenFehler && isAdmin && hatBestandteile(newItem) && neuKontingentFehler && (
                                        <p role="alert" className="text-[11px] font-semibold text-red-700 mt-2 text-center">
                                            Stundenkontingent: {neuKontingentFehler}
                                        </p>
                                    )}
                                    {isAdmin && <p className="text-[10px] text-slate-400 mt-2 text-center">Wird im globalen Firmen-Katalog gespeichert.</p>}
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-slate-50 p-3 border-t border-slate-200 flex justify-between items-center">
                             <div className="text-xs text-slate-400 italic pl-2">
                                 {selectedServices.length} Positionen im Projekt-LV
                             </div>
                             <div className="flex gap-2">
                                <Button variant="secondary" onClick={() => {
                                    if(selectionBuffer.size === 0) return;
                                    const source = activeSourceItems;
                                    const newItems: ServiceItem[] = [];
                                    selectionBuffer.forEach((qty, pos) => {
                                        const t = source.find(c => c.lvPosition === pos);
                                        if(t) newItems.push(mitVorlagenKopie({ ...t, id: crypto.randomUUID(), quantityContracted: qty, quantityUsed: 0, status: 'open' as const }));
                                    });
                                    setSelectedServices(prev => [...prev, ...newItems]);
                                    setSelectionBuffer(new Map());
                                    setIsCatalogOpen(false);
                                }} disabled={selectionBuffer.size === 0}>
                                    Auswahl übernehmen ({selectionBuffer.size})
                                </Button>
                                <Button variant="primary" onClick={() => setIsCatalogOpen(false)}>Fertig</Button>
                             </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
